import React from 'react';
import { connect } from 'react-redux';
import {
  Box, Typography, Button, Chip, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip,
  Tabs, Tab, TextField, Switch, FormControlLabel, Divider, MenuItem,
} from '@mui/material';
import AceEditor from 'react-ace';
import 'ace-builds/webpack-resolver';
import 'ace-builds/src-noconflict/mode-yaml';
import 'ace-builds/src-noconflict/theme-monokai';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { getPartnerGuid, hasAuthourity } from '../../lib/access.es6';
import {
  getEngineFlowsRequest,
  getEngineFlowRawRequest,
  getEngineFlowRawReset,
  publishEngineFlowRequest,
  publishEngineFlowReset,
  createEngineFlowRequest,
  createEngineFlowReset,
} from './actions.es6';

var TH = {
  fontSize: 11, fontWeight: 700, color: '#6B7280',
  textTransform: 'uppercase', letterSpacing: '0.05em',
  bgcolor: '#F9FAFB', borderBottom: '1px solid #E5E7EB',
  py: 1.25, px: 2,
};
var TD = {
  fontSize: 13, color: '#374151',
  borderBottom: '1px solid #F3F4F6',
  py: 1.5, px: 2,
};

var FLOW_TYPES = [
  'PolicyOnboarding',
  'PolicyAmendment',
  'ClaimInitiation',
  'PolicyCancellation',
  'PolicyReissue',
  'PolicyCallback',
];

var STEP_TYPES = [
  'assert', 'build_schedule_entries', 'cancel_policy', 'change_quotation',
  'compute_risk', 'confirm_quotation', 'create_bank_details', 'create_beneficiary',
  'create_note', 'create_quotation', 'enrich_claims_with_dependant_names',
  'evaluate_quote', 'fetch_kyc', 'http_call', 'initiate_claim', 'initiate_payment',
  'list_payments', 'load_benefit_balances', 'load_customer_policies',
  'load_debit_order_requests', 'load_debit_order', 'load_event_quotation',
  'load_group', 'load_policy_by_number', 'load_policy_claims', 'load_policy',
  'load_product', 'publish_amqp_payment', 'publish_debit_order', 'purchase_policy',
  'purchase_simple_policy', 'register_customer', 'register_group_customer',
  'reinstate_policy', 'run_hook', 'set_value', 'trigger_group_policy_onboarding',
  'update_bank_details', 'update_customer', 'update_policy_beneficiary',
  'update_policy', 'update_quotation', 'validate_input',
];

function parseYaml(str) {
  try {
    var jsYaml = require('js-yaml');
    var load = jsYaml.load || jsYaml.safeLoad;
    return load(str) || {};
  } catch (e) {
    return null;
  }
}

function dumpYaml(obj) {
  try {
    var jsYaml = require('js-yaml');
    var dump = jsYaml.dump || jsYaml.safeDump;
    return dump(obj, { lineWidth: 120, noRefs: true });
  } catch (e) {
    return '';
  }
}

function applyFormChange(yamlStr, updater) {
  var parsed = parseYaml(yamlStr);
  if (!parsed) return yamlStr;
  updater(parsed);
  return dumpYaml(parsed);
}

function newFlowTemplate(partner) {
  return [
    `version: '1.0'`,
    `id: ${partner}_new_flow`,
    `partner: ${partner}`,
    `name: New Flow Name`,
    `type: PolicyOnboarding`,
    `steps: []`,
  ].join('\n');
}

class EngineFlows extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      editorOpen: false,
      isNewFlow: false,
      selectedFlow: null,
      editorContent: '',
      hasYamlErrors: false,
      activeTab: 0,
    };
    this.patch = this.patch.bind(this);
  }

  patch(updater) {
    // Run applyFormChange synchronously so closures over synthetic events fire
    // before React 16 pools them (e.target would be null inside a deferred updater).
    this.setState({ editorContent: applyFormChange(this.state.editorContent, updater) });
  }

  componentWillMount() {
    this.props.dispatch(getEngineFlowsRequest(getPartnerGuid()));
  }

  componentDidUpdate(prevProps) {
    const { engine_flow_raw, successfully_published, successfully_created } = this.props;

    if (engine_flow_raw && engine_flow_raw !== prevProps.engine_flow_raw) {
      this.setState({ editorContent: engine_flow_raw });
    }

    if (successfully_published && successfully_published !== prevProps.successfully_published) {
      this.closeEditor();
    }

    if (successfully_created && successfully_created !== prevProps.successfully_created) {
      this.closeEditor();
    }
  }

  openEdit(flow) {
    this.props.dispatch(getEngineFlowRawReset());
    this.props.dispatch(publishEngineFlowReset());
    this.setState({ editorOpen: true, isNewFlow: false, selectedFlow: flow, editorContent: '', activeTab: 0 });
    this.props.dispatch(getEngineFlowRawRequest(getPartnerGuid(), flow.id));
  }

  openCreate() {
    this.props.dispatch(getEngineFlowRawReset());
    this.props.dispatch(createEngineFlowReset());
    this.setState({
      editorOpen: true,
      isNewFlow: true,
      selectedFlow: null,
      editorContent: newFlowTemplate(getPartnerGuid()),
      activeTab: 0,
    });
  }

  closeEditor() {
    this.setState({ editorOpen: false, selectedFlow: null, editorContent: '', activeTab: 0 });
    this.props.dispatch(getEngineFlowRawReset());
    this.props.dispatch(publishEngineFlowReset());
    this.props.dispatch(createEngineFlowReset());
  }

  handlePublish() {
    const { editorContent, isNewFlow, selectedFlow } = this.state;
    const partner = getPartnerGuid();
    if (isNewFlow) {
      this.props.dispatch(createEngineFlowRequest(partner, editorContent));
    } else {
      this.props.dispatch(publishEngineFlowRequest(partner, selectedFlow.id, editorContent));
    }
  }

  renderUiTab() {
    const { editorContent, isNewFlow } = this.state;
    const parsed = parseYaml(editorContent);
    if (!parsed) {
      return (
        <Alert severity="warning">
          YAML has syntax errors — switch to the YAML tab to fix them before editing here.
        </Alert>
      );
    }

    const vars = parsed.vars || {};
    const steps = Array.isArray(parsed.steps) ? parsed.steps : [];

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Basic info */}
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6B7280' }}>
            Basic info
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.5 }}>
            <TextField
              label="ID"
              size="small"
              value={parsed.id || ''}
              disabled={!isNewFlow}
              onChange={e => this.patch(p => { p.id = e.target.value; })}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
              sx={{ width: 200 }}
            />
            <TextField
              label="Name"
              size="small"
              value={parsed.name || ''}
              onChange={e => this.patch(p => { p.name = e.target.value; })}
              sx={{ width: 220 }}
            />
            <TextField
              select
              label="Type"
              size="small"
              value={parsed.type || ''}
              onChange={e => this.patch(p => { p.type = e.target.value; })}
              sx={{ width: 180 }}
            >
              {FLOW_TYPES.map(t => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Version"
              size="small"
              value={parsed.version || ''}
              placeholder="1.0"
              onChange={e => this.patch(p => { p.version = e.target.value; })}
              sx={{ width: 100 }}
            />
            <TextField
              label="Product code"
              size="small"
              value={parsed.product_code || ''}
              placeholder="optional"
              onChange={e => this.patch(p => { p.product_code = e.target.value || undefined; })}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
              sx={{ width: 160 }}
            />
          </Box>
          <TextField
            label="Description"
            size="small"
            fullWidth
            multiline
            rows={3}
            value={parsed.description || ''}
            placeholder="Optional description"
            onChange={e => this.patch(p => { p.description = e.target.value || undefined; })}
            sx={{ mt: 2 }}
          />
        </Box>

        <Divider />

        {/* Flags */}
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6B7280' }}>
            Flags
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 3, mt: 1.5 }}>
            <TextField
              label="Async boundary step ID"
              size="small"
              value={parsed.async_boundary || ''}
              placeholder="step_id (optional)"
              onChange={e => this.patch(p => { p.async_boundary = e.target.value || undefined; })}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
              sx={{ width: 220 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!parsed.expose_unmapped_fields}
                  onChange={e => this.patch(p => { if (e.target.checked) p.expose_unmapped_fields = true; else delete p.expose_unmapped_fields; })}
                  size="small"
                />
              }
              label={<Typography variant="body2">Expose unmapped fields</Typography>}
            />
          </Box>
        </Box>

        <Divider />

        {/* Variables */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6B7280' }}>
              Variables (@alias)
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              variant="outlined"
              sx={{ textTransform: 'none', fontSize: 12 }}
              onClick={() => this.patch(p => {
                if (!p.vars || typeof p.vars !== 'object') p.vars = {};
                p.vars[''] = '';
              })}
            >
              Add
            </Button>
          </Box>
          {Object.keys(vars).length > 0 ? (
            <Box sx={{ border: '1px solid #E5E7EB', borderRadius: 1, overflow: 'hidden' }}>
              {Object.entries(vars).map(([k, v], i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderBottom: i < Object.keys(vars).length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                  <input
                    style={{ width: 140, fontFamily: 'monospace', fontSize: 12, border: 'none', outline: 'none', background: 'transparent' }}
                    value={k}
                    placeholder="alias"
                    onChange={e => this.patch(p => {
                      const m = p.vars;
                      const val = m[k];
                      delete m[k];
                      m[e.target.value] = val || '';
                    })}
                  />
                  <Typography sx={{ color: '#9CA3AF', fontSize: 12 }}>→</Typography>
                  <input
                    style={{ flex: 1, fontFamily: 'monospace', fontSize: 12, border: 'none', outline: 'none', background: 'transparent' }}
                    value={v}
                    placeholder="payload.field or expression"
                    onChange={e => this.patch(p => { p.vars[k] = e.target.value; })}
                  />
                  <IconButton size="small" sx={{ color: '#EF4444', opacity: 0.7 }} onClick={() => this.patch(p => { delete p.vars[k]; })}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: 12 }}>No variables defined.</Typography>
          )}
        </Box>

        <Divider />

        {/* Steps */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6B7280' }}>
              Steps ({steps.length})
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              variant="outlined"
              sx={{ textTransform: 'none', fontSize: 12 }}
              onClick={() => this.patch(p => {
                if (!Array.isArray(p.steps)) p.steps = [];
                p.steps.push({ id: '', type: 'set_value' });
              })}
            >
              Add step
            </Button>
          </Box>
          {steps.length > 0 ? (
            <Box sx={{ border: '1px solid #E5E7EB', borderRadius: 1, overflow: 'hidden' }}>
              {steps.map((s, i) => this.renderStepCard(s, i))}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: 12 }}>No steps defined.</Typography>
          )}
        </Box>
      </Box>
    );
  }

  renderStepCard(step, index) {
    var self = this;

    function stepPatch(updater) {
      self.patch(function(p) { updater(p.steps[index]); });
    }

    var withParams = (step.with && typeof step.with === 'object' && !Array.isArray(step.with))
      ? step.with
      : {};

    return (
      <details key={index} style={{ borderBottom: '1px solid #F3F4F6' }}>
        <summary style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer', listStyle: 'none' }}>
          <Typography sx={{ flex: 1, fontFamily: 'monospace', fontSize: 12, fontWeight: 500 }}>
            {step.id || `step_${index + 1}`}
          </Typography>
          <Chip label={step.type || '—'} size="small" sx={{ fontFamily: 'monospace', fontSize: 10, height: 20 }} />
          <IconButton size="small" sx={{ color: '#EF4444', opacity: 0.6 }} onClick={(e) => {
            e.preventDefault();
            self.patch(function(p) { p.steps.splice(index, 1); });
          }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </summary>
        <Box sx={{ px: 2, py: 1.5, bgcolor: '#FAFAFA', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <TextField
              label="ID"
              size="small"
              fullWidth
              value={step.id || ''}
              placeholder="step_id"
              onChange={e => stepPatch(s => { s.id = e.target.value; })}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
            />
            <TextField
              select
              label="Type"
              size="small"
              fullWidth
              value={step.type || ''}
              onChange={e => stepPatch(s => { s.type = e.target.value; })}
            >
              <MenuItem value="">— select —</MenuItem>
              {STEP_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Box>
          <TextField
            label="Description"
            size="small"
            fullWidth
            value={step.description || ''}
            onChange={e => stepPatch(s => { s.description = e.target.value || undefined; })}
          />
          <TextField
            label="Condition"
            size="small"
            fullWidth
            value={step.condition || ''}
            placeholder="optional expression"
            onChange={e => stepPatch(s => { s.condition = e.target.value || undefined; })}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
          />
          {step.type === 'assert' && (
            <Box sx={{ border: '1px solid #FDE68A', borderRadius: 1, bgcolor: '#FFFBEB', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField
                label="Assert expression"
                size="small"
                fullWidth
                multiline
                rows={2}
                value={step.assert || ''}
                placeholder="expression that must be truthy"
                onChange={e => stepPatch(s => { s.assert = e.target.value || undefined; })}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
              />
              <TextField
                label="Error message"
                size="small"
                fullWidth
                value={(step.error && step.error.message) || step.error || ''}
                placeholder="optional error message"
                onChange={e => stepPatch(s => { s.error = e.target.value ? { message: e.target.value } : undefined; })}
              />
            </Box>
          )}
          {step.type === 'set_value' && (
            <Box sx={{ border: '1px solid #BAE6FD', borderRadius: 1, bgcolor: '#F0F9FF', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField
                label="Save as"
                size="small"
                fullWidth
                value={step.save_as || ''}
                placeholder="variable_name"
                onChange={e => stepPatch(s => { s.save_as = e.target.value || undefined; })}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
              />
              <TextField
                label="Value"
                size="small"
                fullWidth
                multiline
                rows={2}
                value={step.value || ''}
                placeholder="expression or literal"
                onChange={e => stepPatch(s => { s.value = e.target.value || undefined; })}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
              />
            </Box>
          )}
          {step.type === 'run_hook' && (
            <TextField
              label="Hook"
              size="small"
              fullWidth
              value={step.hook || ''}
              placeholder="hook_name"
              onChange={e => stepPatch(s => { s.hook = e.target.value || undefined; })}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
            />
          )}
          {/* With params */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6B7280', fontSize: 10 }}>
                With params
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                variant="text"
                sx={{ textTransform: 'none', fontSize: 11, minWidth: 0 }}
                onClick={() => stepPatch(s => {
                  if (!s.with || typeof s.with !== 'object') s.with = {};
                  s.with[''] = '';
                })}
              >
                Add
              </Button>
            </Box>
            {Object.keys(withParams).length > 0 ? (
              <Box sx={{ border: '1px solid #E5E7EB', borderRadius: 1, overflow: 'hidden' }}>
                {Object.entries(withParams).map(([k, v], wi) => (
                  <Box key={wi} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, borderBottom: wi < Object.keys(withParams).length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                    <input
                      style={{ width: 130, fontFamily: 'monospace', fontSize: 12, border: 'none', outline: 'none', background: 'transparent' }}
                      value={k}
                      placeholder="param"
                      onChange={e => stepPatch(s => {
                        var m = s.with;
                        var val = m[k];
                        delete m[k];
                        m[e.target.value] = val || '';
                      })}
                    />
                    <Typography sx={{ color: '#9CA3AF', fontSize: 12 }}>→</Typography>
                    <input
                      style={{ flex: 1, fontFamily: 'monospace', fontSize: 12, border: 'none', outline: 'none', background: 'transparent' }}
                      value={v}
                      placeholder="value or expression"
                      onChange={e => stepPatch(s => { s.with[k] = e.target.value; })}
                    />
                    <IconButton size="small" sx={{ color: '#EF4444', opacity: 0.6 }} onClick={() => stepPatch(s => { delete s.with[k]; })}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: 11 }}>No params.</Typography>
            )}
          </Box>
        </Box>
      </details>
    );
  }

  render() {
    if (!hasAuthourity('VIEW_UNDERWRITING_FLOWS')) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            You do not have permission to view Underwriting Flows.
          </Typography>
        </Box>
      );
    }

    const {
      loader, engine_flows, get_flows_error,
      flow_raw_loader, get_flow_raw_error,
      publish_loader, publish_error,
      create_loader, create_error,
    } = this.props;
    const { editorOpen, isNewFlow, selectedFlow, editorContent, hasYamlErrors, activeTab } = this.state;

    const saving = publish_loader || create_loader;
    const saveError = publish_error || create_error;

    return (
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountTreeOutlinedIcon sx={{ color: '#6B7280' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>
              Underwriting Flows
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => this.openCreate()}
            sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' }, textTransform: 'none' }}
          >
            New Flow
          </Button>
        </Box>

        {/* Error */}
        {get_flows_error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load flows. Please try again.
          </Alert>
        )}

        {/* Flows table */}
        {loader ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ border: '1px solid #E5E7EB', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={TH}>Name</TableCell>
                  <TableCell sx={TH}>ID</TableCell>
                  <TableCell sx={TH}>Type</TableCell>
                  <TableCell sx={TH}>Product Code</TableCell>
                  <TableCell sx={TH}>Source</TableCell>
                  <TableCell sx={TH} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!engine_flows || engine_flows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ ...TD, textAlign: 'center', color: '#9CA3AF', py: 4 }}>
                      No flows found for this partner.
                    </TableCell>
                  </TableRow>
                ) : (
                  engine_flows.map(flow => (
                    <TableRow key={flow.id} hover>
                      <TableCell sx={{ ...TD, fontWeight: 500 }}>{flow.name}</TableCell>
                      <TableCell sx={{ ...TD, fontFamily: 'monospace', fontSize: 12 }}>{flow.id}</TableCell>
                      <TableCell sx={TD}>
                        <Chip label={flow.type} size="small" sx={{ fontSize: 11 }} />
                      </TableCell>
                      <TableCell sx={{ ...TD, color: '#9CA3AF' }}>
                        {flow.product_code || '—'}
                      </TableCell>
                      <TableCell sx={TD}>
                        <Chip
                          label={flow.source}
                          size="small"
                          color={flow.source === 's3' ? 'success' : 'default'}
                          sx={{ fontSize: 11 }}
                        />
                      </TableCell>
                      <TableCell sx={TD} align="right">
                        <Tooltip title="Edit flow">
                          <IconButton size="small" onClick={() => this.openEdit(flow)} sx={{ color: '#0D47A1' }}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Editor Dialog */}
        <Dialog
          open={editorOpen}
          onClose={() => this.closeEditor()}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { height: '85vh' } }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {isNewFlow ? 'New Flow' : (selectedFlow && selectedFlow.name)}
              </Typography>
              {!isNewFlow && selectedFlow && (
                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6B7280' }}>
                  {selectedFlow.id}
                </Typography>
              )}
            </Box>
            <IconButton size="small" onClick={() => this.closeEditor()}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
            {/* Alerts */}
            {saveError && (
              <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
                {(saveError && saveError.message) || 'Failed to save flow. Check your YAML is valid.'}
              </Alert>
            )}
            {get_flow_raw_error && (
              <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
                Failed to load flow content.
              </Alert>
            )}

            {/* Tabs */}
            <Box sx={{ borderBottom: '1px solid #E5E7EB', px: 2 }}>
              <Tabs value={activeTab} onChange={(_, v) => this.setState({ activeTab: v })} textColor="primary" indicatorColor="primary">
                <Tab label="UI" sx={{ textTransform: 'none', fontWeight: 600, fontSize: 13 }} />
                <Tab label="YAML" sx={{ textTransform: 'none', fontWeight: 600, fontSize: 13 }} />
              </Tabs>
            </Box>

            {/* Tab content */}
            {activeTab === 1 ? (
              flow_raw_loader ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  <AceEditor
                    mode="yaml"
                    theme="monokai"
                    value={editorContent}
                    onChange={value => this.setState({ editorContent: value })}
                    onValidate={annotations => this.setState({ hasYamlErrors: annotations.some(a => a.type === 'error') })}
                    name="engine-flow-yaml-editor"
                    width="100%"
                    height="100%"
                    fontSize={13}
                    showPrintMargin={false}
                    showGutter={true}
                    highlightActiveLine={true}
                    editorProps={{ $blockScrolling: true }}
                    setOptions={{
                      enableBasicAutocompletion: true,
                      enableLiveAutocompletion: false,
                      showLineNumbers: true,
                      tabSize: 2,
                      useWorker: true,
                    }}
                  />
                </Box>
              )
            ) : (
              flow_raw_loader ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                  {this.renderUiTab()}
                </Box>
              )
            )}
          </DialogContent>

          <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #E5E7EB' }}>
            <Button onClick={() => this.closeEditor()} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CloudUploadOutlinedIcon />}
              onClick={() => this.handlePublish()}
              disabled={saving || flow_raw_loader || !editorContent || hasYamlErrors}
              sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' }, textTransform: 'none' }}
            >
              {saving ? 'Publishing…' : 'Publish'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

function mapStateToProps(state) {
  return {
    loader: state.engineFlows.loader,
    engine_flows: state.engineFlows.engine_flows,
    get_flows_error: state.engineFlows.get_flows_error,

    flow_raw_loader: state.engineFlows.flow_raw_loader,
    engine_flow_raw: state.engineFlows.engine_flow_raw,
    get_flow_raw_error: state.engineFlows.get_flow_raw_error,

    publish_loader: state.engineFlows.publish_loader,
    successfully_published: state.engineFlows.successfully_published,
    publish_error: state.engineFlows.publish_error,

    create_loader: state.engineFlows.create_loader,
    successfully_created: state.engineFlows.successfully_created,
    create_error: state.engineFlows.create_error,
  };
}

export default connect(mapStateToProps)(EngineFlows);
